import { useState, type ReactNode } from 'react'
import { Link } from '@tanstack/react-router'
import { Button } from '@/components/ui/button'
import {
  isBusy,
  NO_LABEL,
  useRunExamples,
  useSetupRun,
  useSortNewMail,
  useStopRun,
  useUndoRun,
  type RunResults as Results,
} from '@/api/setup-runs'
import { mailboxWords } from '@/lib/mailbox-words'

type Words = ReturnType<typeof mailboxWords>

type Row = {
  key: string
  name: string
  count: number
  none?: boolean
  other?: boolean
}

// Rows for earlier changes of ours that the run put back.
const PUT_BACK = /^(Back in the inbox|Marked unread|Old (label|category) removed)/

const day = (value: string) =>
  new Date(value).toLocaleDateString(undefined, {
    month: 'short',
    day: 'numeric',
  })

function plural(count: number, one: string, many = `${one}s`) {
  return `${count.toLocaleString()} ${count === 1 ? one : many}`
}

const capital = (text: string) => text.charAt(0).toUpperCase() + text.slice(1)

/** The provider's own word for the inboxes on show: labels on Gmail, categories on Outlook. */
function wordsFor(run: Results, inbox: string | null): Words {
  return mailboxWords(
    run.inboxes
      .filter((item) => !inbox || item.inbox_id === inbox)
      .map((item) => item.kind),
  )
}

/** Why undo couldn't put emails back, in the inbox's own terms. */
function failedLine(state: Results['undo']): string {
  const lead = `${plural(state.failed, 'email')} couldn't be put back`
  const reasons = state.failures ?? []
  if (reasons.length === 0) return `${lead}.`
  const [only] = reasons
  if (reasons.length === 1 && only) return `${lead} because ${only.reason}.`
  return `${lead}: ${reasons.map((item) => `${item.emails.toLocaleString()} because ${item.reason}`).join('; ')}.`
}

function undoneLine(run: Results, words: Words): string {
  const { undo: state } = run
  return [
    state.left_alone &&
      `${plural(state.left_alone, 'email')} you had changed ${state.left_alone === 1 ? 'was' : 'were'} left alone.`,
    run.other_actions.some((row) => PUT_BACK.test(row.name)) &&
      'Earlier changes it had put back are restored.',
    state.failed && failedLine(state),
    run.created_labels &&
      (state.delete_created
        ? `The ${plural(run.created_labels, words.one, words.many)} and their rules were deleted.`
        : `The ${plural(run.created_labels, words.one, words.many)} ${run.created_labels === 1 ? 'is' : 'are'} still there, unused.`),
  ]
    .filter(Boolean)
    .join(' ')
}

function counts(run: Results, inbox: string | null, words: Words) {
  const pick = (byInbox: Record<string, number>, total: number) =>
    inbox ? (byInbox[inbox] ?? 0) : total
  const inboxes = inbox
    ? run.inboxes.filter((item) => item.inbox_id === inbox)
    : run.inboxes
  const processed = inboxes.reduce((sum, item) => sum + item.processed, 0)
  const labelled = inboxes.reduce((sum, item) => sum + item.labelled, 0)
  const rows: Row[] = run.labels.map((label) => ({
    key: label.name,
    name: label.name,
    count: pick(label.counts, label.total),
  }))
  if (run.labels.length)
    rows.push({
      key: NO_LABEL,
      name: words.none,
      count: processed - labelled,
      none: true,
    })
  rows.push(
    ...run.other_actions.map((action) => ({
      key: `action:${action.name}`,
      name: action.name,
      count: pick(action.counts, action.total),
      other: true,
    })),
  )
  return { processed, labelled, rows: rows.filter((row) => row.count > 0) }
}

function Examples({
  runId,
  label,
  inbox,
}: {
  runId: string
  label: string
  inbox: string | null
}) {
  const examples = useRunExamples(runId, label, inbox ?? undefined)
  if (examples.isLoading)
    return <p className="mt-2 text-xs text-muted-foreground">Loading…</p>
  if (!examples.data?.length)
    return (
      <p className="mt-2 text-xs text-muted-foreground">
        No email details to show.
      </p>
    )
  return (
    <ul className="mt-2 space-y-1 text-xs">
      {examples.data.map((email, index) => (
        <li key={index} className="flex min-w-0 justify-between gap-3">
          <span className="truncate">
            {email.subject || email.sender || 'No subject'}
          </span>
          <span className="shrink-0 text-muted-foreground">
            {day(email.received_at)}
          </span>
        </li>
      ))}
    </ul>
  )
}

function Bars({
  run,
  inbox,
  words,
}: {
  run: Results
  inbox: string | null
  words: Words
}) {
  const [open, setOpen] = useState<string | null>(null)
  const { rows } = counts(run, inbox, words)
  const max = Math.max(1, ...rows.map((row) => row.count))
  return (
    <ul className="mt-2">
      {rows.map((row) => {
        const expanded = open === row.key
        const examples = !row.key.startsWith('action:')
        return (
          <li
            key={row.key}
            className={expanded ? '-mx-2 rounded-lg bg-muted px-2 pb-2' : ''}
          >
            <button
              type="button"
              disabled={!examples}
              aria-expanded={examples ? expanded : undefined}
              title={`${row.name}: ${plural(row.count, 'email')}`}
              onClick={() => setOpen(expanded ? null : row.key)}
              className="block w-full min-w-0 py-1.5 text-left disabled:cursor-default"
            >
              <span className="flex min-w-0 justify-between gap-3 text-sm">
                <span className="truncate font-medium">{row.name}</span>
                <span className="shrink-0 font-semibold tabular-nums">
                  {row.count.toLocaleString()}
                </span>
              </span>
              <span className="mt-1 block h-2.5">
                <span
                  className={`block h-full min-w-1 rounded-r-[4px] ${row.none ? 'bg-muted-foreground/25' : row.other ? 'bg-primary/45' : 'bg-primary'}`}
                  style={{ width: `${(row.count / max) * 100}%` }}
                />
              </span>
            </button>
            {expanded && (
              <Examples runId={run.id} label={row.key} inbox={inbox} />
            )}
          </li>
        )
      })}
    </ul>
  )
}

function Progress({ run, onStop, stopping }: {
  run: Results
  onStop: () => void
  stopping: boolean
}) {
  return (
    <div>
      <div className="flex items-center gap-2">
        <span className="size-2 rounded-full bg-primary motion-safe:animate-pulse" />
        <h3 className="font-semibold">
          {run.kind === 'categorize_plan'
            ? 'Sorting the last 30 days'
            : `Processing ${plural(run.total, 'email')}`}
        </h3>
      </div>
      {run.putting_back && (
        <p className="mt-1 text-sm text-muted-foreground">
          Putting back earlier changes the new rules wouldn&apos;t make…
        </p>
      )}
      <div className="mt-3 space-y-3">
        {run.inboxes.map((inbox) => {
          const done = inbox.processed + inbox.skipped
          return (
            <div key={inbox.inbox_id} className="min-w-0 text-sm">
              <div className="flex justify-between gap-3">
                <span className="truncate">{inbox.name}</span>
                <span className="shrink-0 text-muted-foreground tabular-nums">
                  {done.toLocaleString()} of {inbox.total.toLocaleString()}
                </span>
              </div>
              <div className="mt-1.5 h-1.5 overflow-hidden rounded-full bg-muted">
                <div
                  className="h-full rounded-full bg-primary transition-[width] duration-500"
                  style={{
                    width: `${inbox.total ? (done / inbox.total) * 100 : 0}%`,
                  }}
                />
              </div>
            </div>
          )
        })}
      </div>
      <Button
        variant="outline"
        className="mt-3 w-full"
        disabled={stopping}
        onClick={onStop}
      >
        {stopping ? 'Stopping…' : 'Stop'}
      </Button>
    </div>
  )
}

function Undo({ run, words }: { run: Results; words: Words }) {
  const [confirming, setConfirming] = useState(false)
  const [deleteCreated, setDeleteCreated] = useState(false)
  const undo = useUndoRun(run.id)
  const sort = useSortNewMail(run.id)
  const { undo: state } = run
  const changed = run.labelled || run.processed
  if (state.status === 'queued' || state.status === 'running') {
    const handled = state.reverted + state.left_alone + state.failed
    return (
      <p className="mt-3 flex items-center gap-2 text-sm text-muted-foreground">
        <span className="size-2 rounded-full bg-primary motion-safe:animate-pulse" />
        Undoing… {handled ? `${handled.toLocaleString()} done` : ''}
      </p>
    )
  }
  if (confirming)
    return (
      <div className="mt-3 border-t pt-3">
        <h4 className="font-semibold">Undo this run?</h4>
        <p className="mt-1 text-sm text-muted-foreground">
          {run.labels.length
            ? `Takes the ${words.many} off ${plural(run.labelled, 'email')}.`
            : `Puts back what it changed on ${plural(changed, 'email')}.`}{' '}
          Anything you changed yourself since stays as it is.
        </p>
        {run.created_labels > 0 && (
          <label className="mt-3 flex items-start gap-2.5 text-sm">
            <input
              type="checkbox"
              className="mt-0.5 size-4 shrink-0 accent-primary"
              checked={deleteCreated}
              onChange={(event) => setDeleteCreated(event.target.checked)}
            />
            Also delete the{' '}
            {plural(run.created_labels, words.one, words.many)} and their rules
          </label>
        )}
        {undo.isError && (
          <p role="alert" className="mt-2 text-sm text-destructive">
            Undo couldn't start. Try again.
          </p>
        )}
        <div className="mt-3 grid grid-cols-2 gap-2">
          <Button variant="outline" onClick={() => setConfirming(false)}>
            Keep it
          </Button>
          <Button
            variant="destructive"
            disabled={undo.isPending}
            onClick={() => undo.mutate(deleteCreated)}
          >
            Undo
          </Button>
        </div>
      </div>
    )
  const offerSorting = run.inboxes.some((inbox) => !inbox.sorts_new_mail)
  return (
    <div className="mt-3">
      {state.status === 'failed' && (
        <p role="alert" className="mb-2 text-sm text-destructive">
          Undo stopped partway. You can try again.
        </p>
      )}
      {sort.isError && (
        <p role="alert" className="mb-2 text-sm text-destructive">
          New-mail sorting couldn't be turned on. Check the inbox settings.
        </p>
      )}
      <div className={`grid gap-2 ${offerSorting ? 'grid-cols-2' : ''}`}>
        <Button variant="outline" onClick={() => setConfirming(true)}>
          Undo this run
        </Button>
        {offerSorting && (
          <Button disabled={sort.isPending} onClick={() => sort.mutate(false)}>
            Sort new mail too
          </Button>
        )}
      </div>
    </div>
  )
}

export function RunResults({
  runId,
  page = false,
  fallback = null,
}: {
  runId: string
  page?: boolean
  /** Shown instead when there's no run to show, e.g. the chat's progress row. */
  fallback?: ReactNode
}) {
  const query = useSetupRun(runId)
  const stop = useStopRun(runId)
  const [inbox, setInbox] = useState<string | null>(null)
  const run = query.data
  if (query.isLoading)
    return (
      <div className="h-24 animate-pulse rounded-xl border bg-card motion-reduce:animate-none" />
    )
  if (query.isError)
    return (
      <p role="alert" className="text-sm text-muted-foreground">
        The results couldn&apos;t be loaded.
      </p>
    )
  // Nothing to show when there's no run, or it ended before any email was queued;
  // the chat's progress row explains a run that couldn't start.
  if (!run || (run.queued === 0 && run.status !== 'running'))
    return page ? (
      <p className="text-sm text-muted-foreground">
        This run didn&apos;t process any emails.
      </p>
    ) : (
      fallback
    )
  const shell = page ? 'min-w-0' : 'min-w-0 rounded-xl border bg-card p-4'
  if (run.status === 'running' && run.undo.status === 'none')
    return (
      <div className={shell}>
        <Progress
          run={run}
          onStop={() => stop.mutate(undefined)}
          stopping={stop.isPending || stop.isSuccess}
        />
      </div>
    )
  const words = wordsFor(run, inbox)
  if (run.undo.status === 'done')
    return (
      <div className={shell}>
        <p className="text-sm text-muted-foreground">
          {run.kind === 'categorize_plan' ? 'Last 30 days' : 'Older mail'} ·{' '}
          {day(run.window_start)} – {day(run.window_end)}
        </p>
        <p className="mt-0.5 text-2xl font-bold tracking-tight text-balance">
          Undone:{' '}
          {run.labels.length
            ? `${words.many} removed from ${plural(run.undo.reverted, 'email')}`
            : `changes put back on ${plural(run.undo.reverted, 'email')}`}
        </p>
        {undoneLine(run, words) && (
          <p className="mt-2 text-sm text-muted-foreground">
            {undoneLine(run, words)}
          </p>
        )}
      </div>
    )
  const { processed, labelled } = counts(run, inbox, words)
  const labelTotals = run.labels.reduce((sum, label) => sum + label.total, 0)
  const notes = [
    run.status === 'stopped' &&
      `Stopped after ${plural(run.processed + run.skipped, 'email')} of ${run.total.toLocaleString()}.`,
    labelTotals > run.labelled &&
      `Some emails got more than one ${words.one}.`,
    run.skipped &&
      `${plural(run.skipped, 'email')} couldn't be read and ${run.skipped === 1 ? 'was' : 'were'} skipped.`,
  ].filter(Boolean)
  return (
    <div className={shell}>
      <p className="text-sm text-muted-foreground">
        {run.kind === 'categorize_plan' ? 'Last 30 days' : 'Older mail'} ·{' '}
        {day(run.window_start)} – {day(run.window_end)}
      </p>
      <p className="mt-0.5 text-2xl font-bold tracking-tight text-balance">
        {run.labels.length
          ? `${labelled.toLocaleString()} of ${plural(processed, 'email')} ${words.done}`
          : `${plural(processed, 'email')} processed`}
      </p>
      {run.inboxes.length > 1 && (
        <div
          role="group"
          aria-label="Inbox"
          className="-mx-1 mt-3 flex gap-1.5 overflow-x-auto px-1 pb-1 [scrollbar-width:none]"
        >
          {[null, ...run.inboxes.map((item) => item.inbox_id)].map((id) => (
            <button
              key={id ?? 'all'}
              type="button"
              aria-pressed={inbox === id}
              onClick={() => setInbox(id)}
              className="shrink-0 rounded-full border px-3 py-1.5 text-xs aria-pressed:border-foreground aria-pressed:bg-foreground aria-pressed:text-background"
            >
              {id
                ? run.inboxes.find((item) => item.inbox_id === id)?.name
                : run.inboxes.length === 2
                  ? 'Both inboxes'
                  : 'All inboxes'}
            </button>
          ))}
        </div>
      )}
      <Bars run={run} inbox={inbox} words={words} />
      {notes.length > 0 && (
        <p className="mt-3 border-t pt-3 text-sm text-muted-foreground">
          {notes.join(' ')}
        </p>
      )}
      {page && run.inboxes.length > 1 && (
        <table className="mt-4 w-full text-sm">
          <thead>
            <tr className="text-left text-xs text-muted-foreground">
              <th className="pb-2 font-medium">Inbox</th>
              <th className="pb-2 text-right font-medium">Emails</th>
              <th className="pb-2 text-right font-medium">
                {capital(wordsFor(run, null).done)}
              </th>
            </tr>
          </thead>
          <tbody className="tabular-nums">
            {run.inboxes.map((item) => (
              <tr key={item.inbox_id} className="border-t">
                <td className="max-w-0 truncate py-2">{item.name}</td>
                <td className="py-2 text-right">
                  {item.total.toLocaleString()}
                </td>
                <td className="py-2 text-right">
                  {item.labelled.toLocaleString()}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
      <Undo run={run} words={words} />
      {!page && (
        <Link
          to="/runs/$runId"
          params={{ runId }}
          className="mt-3 inline-block text-sm font-medium text-primary underline underline-offset-4"
        >
          Open results
        </Link>
      )}
      {isBusy(run) && <span className="sr-only">Updating</span>}
    </div>
  )
}
